import Vue from "vue";
import VueRouter, { RouteConfig, Route } from "vue-router";

import Login from "../views/Login.vue";
import Dashboard from "../views/Dashboard.vue";
const Inventory = () =>
  import(
    /* webpackChunkName: "inventory" */ "../components/Inventory/Inventory.vue"
  );
const ManageProducts = () =>
  import(
    /* webpackChunkName: "inventory" */ "../components/Inventory/ManageProducts.vue"
  );
const ManageSkus = () =>
  import(
    /* webpackChunkName: "inventory" */ "../components/Inventory/ManageSkus.vue"
  );
const ManageSamples = () =>
  import(
    /* webpackChunkName: "inventory" */ "../components/Inventory/ManageSamples.vue"
  );
const InventorySearch = () =>
  import(
    /* webpackChunkName: "search" */ "../components/AddInventory/InventorySearch.vue"
  );
const SkusList = () =>
  import(
    /* webpackChunkName: "search" */ "../components/AddInventory/SkusList.vue"
  );
const Finish = () =>
  import(
    /* webpackChunkName: "search" */ "../components/AddInventory/Finish.vue"
  );
const TrxnHistory = () =>
  import(
    /* webpackChunkName: "history" */ "../components/TransactionHistory/TrxnHistory.vue"
  );
const TrxnHistoryTable = () =>
  import(
    /* webpackChunkName: "history" */ "../components/TransactionHistory/TrxnHistoryTable.vue"
  );
import DefaultPage from "../views/DefaultPage.vue";
import { AuthPlugin } from "@canvas/authplugin";
import AuthError from "../views/AuthError.vue";
import store from "../store";

function transformUserDetails(userDetails: { [key: string]: any }) {
  return {
    id: userDetails.id,
    name: userDetails.display_name,
    display_name: userDetails.display_name,
    email: userDetails.email,
    phone: userDetails.phone,
    image_url: userDetails.image_url,
    city: userDetails.city,
    hasWriteAccess: false,
    hasReadAccess: false
  };
}

async function authenticationGuard(
  to: Route,
  from: Route,
  next: (arg?: any) => any
) {
  const auth = AuthPlugin.getAuth();
  if (!auth.IsLoggedIn) {
    await auth.authorize();
    if (auth.UserDetails) {
      const userQuery = "include=roles,city";
      const userDetails = await store.dispatch(
        "appModule/fetchBouncerUserDetails",
        { user_id: auth.UserDetails.id, query: userQuery }
      );
      const transformedUserDetails = transformUserDetails(userDetails);
      //fetch user's read || write access
      const userPermisssions = await store.dispatch(
        "appModule/userPermissionsCheck"
      );
      if (!userPermisssions.hasReadAccess && !userPermisssions.hasWriteAccess) {
        next("/autherror");
      }
      store.commit(
        "appModule/setLoggedInUser",
        Object.assign(transformedUserDetails, userPermisssions)
      );
      store.dispatch(
        "appModule/identifyUserInSegment",
        store.state.appModule.loggedInUser
      );
      //users that have write access can view only EC's that they are part of.
      //users that have only read access can view all EC's inventory
      if (transformedUserDetails.hasWriteAccess) {
        await store.dispatch("appModule/getEcInfoOfUser");
      } else {
        await store.dispatch("appModule/getAllEcs");
      }
      next();
    }
  }
}

async function logoutUser(to: Route, from: Route, next: (arg?: any) => any) {
  const auth = AuthPlugin.getAuth();
  const logoutUrl = auth.logOut();
  store.dispatch("appModule/trackSegementEvents", {
    eventName: "LOGOUT"
  });
  if (logoutUrl) {
    window.location.assign(logoutUrl);
  } else {
    next("/login");
  }
}

Vue.use(VueRouter);
const routes: RouteConfig[] = [
  {
    path: "/",
    component: Dashboard,
    beforeEnter: authenticationGuard,
    children: [
      {
        path: ":ecId/inventory",
        component: Inventory,
        name: "inventory",
        children: [
          {
            path: ":searchQuery",
            component: ManageProducts,
            name: "products",
            beforeEnter: (to, from, next) => {
              //get the products with the query
              if (to.params.searchQuery) {
                const paramsJSON = JSON.parse(to.params.searchQuery);
                store.dispatch(
                  "appModule/searchInventoryProducts",
                  paramsJSON.pageConfig
                );
              }
              next();
            }
          },
          {
            path: ":searchQuery/:productQuery/skus",
            component: ManageSkus,
            name: "skus",
            beforeEnter: (to, from, next) => {
              //get the skus with the query
              if (to.params.productQuery) {
                const paramsJSON = JSON.parse(to.params.productQuery);
                store.dispatch(
                  "appModule/searchInventorySKUs",
                  paramsJSON.pageConfig
                );
              }
              next();
            }
          },
          {
            path: ":searchQuery/:productQuery/:skuQuery/samples",
            component: ManageSamples,
            name: "samples",
            beforeEnter: (to, from, next) => {
              //get the samples with the query
              if (to.params.skuQuery) {
                const paramsJSON = JSON.parse(to.params.skuQuery);
                store.dispatch(
                  "appModule/searchInventorySamples",
                  paramsJSON.pageConfig
                );
              }
              next();
            }
          }
        ]
      },
      {
        path: ":ecId/add",
        component: InventorySearch,
        name: "addinventory",
        children: [
          {
            path: ":searchQuery",
            component: SkusList,
            name: "skuslist",
            beforeEnter: (to, from, next) => {
              //get CMS product results from the query
              if (to.params.searchQuery) {
                const paramsJSON = JSON.parse(to.params.searchQuery);
                store.dispatch(
                  "appModule/searchCmsProducts",
                  paramsJSON.pageConfig
                );
              }
              next();
            }
          }
        ]
      },
      {
        path: ":ecId/add/:productCode/finishes",
        component: Finish,
        name: "addfinish"
      },
      {
        path: ":ecId/add/:productCode/finishes/:skuCode",
        component: Finish,
        name: "editfinish"
      },
      {
        path: ":ecId/trxnhistory",
        component: TrxnHistory,
        name: "trxnhistory",
        children: [
          {
            path: ":searchQuery",
            component: TrxnHistoryTable,
            name: "trxnhistorysearch",
            beforeEnter: (to, from, next) => {
              //get the lendings history from the query
              if (to.params.searchQuery) {
                const paramsJSON = JSON.parse(to.params.searchQuery);
                store.dispatch(
                  "appModule/searchLendingHistory",
                  paramsJSON.pageConfig
                );
              }
              next();
            }
          }
        ]
      }
    ]
  },
  {
    path: "*",
    component: DefaultPage
  },
  {
    path: "/autherror",
    component: AuthError
  },
  {
    path: "/redirect",
    redirect: (to: Route) => {
      // const REDIRECT_URI = "/search";
      let REDIRECT_URI = JSON.parse(to.query.state as string).pathname;
      if (REDIRECT_URI === "/login" || REDIRECT_URI === "/") {
        REDIRECT_URI = "/";
      }
      const queryString = window.location.search.substring(1);
      const path = [REDIRECT_URI, queryString].join("#");
      // The pkce library needs a full redirect on the browser instead of just
      // a redirect inisde the router. But we also need to return the route
      // to let this function compile
      window.location.assign(path);
      return path;
    }
  },
  {
    path: "/:ecID/logout",
    beforeEnter: logoutUser
  },
  {
    name: "loginpage",
    path: "/login",
    component: Login
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

export default router;
